// slides-library/shared/utils/create-from-slides.ts
//
// WHAT THE FLOATING BAR MAY OFFER FOR A HANDFUL OF PICKED SLIDES.
//
// Three questions the bar used to answer with role flags spelled out inline,
// where a Creator lite and a viewer were often the same `isUserOrCreatorLite`
// and so got the same answer -- which is wrong now that a version is a
// Creator lite's to make and a viewer still makes nothing.
//
// The rules live here so they can be read in one place and tested; the same
// shape as pages/stories/utils/can-edit.ts.

import type { CreateTarget } from "@/features/standalone-versions";

type Roles = {
  isUser: boolean;
  isCreatorLite: boolean;
  /** Standalone versions are on for this org (see features/standalone-versions). */
  versionsOn: boolean;
};

type Where = Roles & {
  /** "library" is the platform; "editor" is the dialog opened from a document. */
  view: "library" | "editor";
  createTarget: CreateTarget;
};

/**
 * What creating from these slides actually makes.
 *
 * A Creator lite makes versions and never stories, so on the platform their
 * answer is "version" whatever the library was opened with -- opened from
 * their own sidebar, nobody has said yet. Everyone else gets what they asked
 * for, and in the editor the question does not arise: the slides are added to
 * the document that is already open.
 */
export const effectiveCreateTarget = ({
  isCreatorLite,
  versionsOn,
  view,
  createTarget,
}: Pick<
  Where,
  "isCreatorLite" | "versionsOn" | "view" | "createTarget"
>): CreateTarget => {
  if (!versionsOn) return "story";
  return isCreatorLite && view === "library" ? "version" : createTarget;
};

/**
 * The bar's primary action: "Add slides" in the editor, "Use slides" or
 * "Create version" out on the platform. A viewer creates nothing, and a
 * Creator lite only a version.
 */
export const canCreateFromSlides = ({
  isUser,
  isCreatorLite,
  versionsOn,
  view,
  createTarget,
}: Where): boolean => {
  if (view === "editor") return true;
  if (isUser) return false;
  // Without standalone versions a Creator lite creates nothing, as before.
  if (!versionsOn) return !isCreatorLite;
  return !isCreatorLite || createTarget === "version";
};

/**
 * "Create with AI": the picked slides become the shape of a new document the
 * model writes. That is a platform action -- in the editor the bar adds the
 * slides as they are -- and it is bounded by the same rule as creating from
 * them by hand, since what comes out is a story or a version either way.
 */
export const canCreateWithAi = ({
  isUser,
  isCreatorLite,
  versionsOn,
  view,
  createTarget,
}: Where): boolean => {
  if (view !== "library") return false;
  if (isUser) return false;
  if (!versionsOn) return !isCreatorLite;
  return !isCreatorLite || createTarget === "version";
};
